// src/pages/user/ProfileUpdate.js
import React, { useState, useEffect, useRef } from 'react';
import { userAuthAPI } from '../../services/api';
import './UserSettings.css';

const Field = ({ name, label, icon, type='text', placeholder, full=false, value, onChange, disabled }) => (
  <div className={`settings-field${full ? ' settings-field-full' : ''}`}>
    <label className="settings-label"><i className={`bi ${icon}`}></i> {label}</label>
    <div className="settings-input-wrapper">
      {name === 'address' ? (
        <textarea name={name} className="settings-input settings-textarea" placeholder={placeholder} value={value} onChange={onChange} disabled={disabled} rows={3}/>
      ) : (
        <input type={type} name={name} className="settings-input" placeholder={placeholder} value={value} onChange={onChange} disabled={disabled}/>
      )}
    </div>
  </div>
);

// Company logo: paste a URL directly, or upload a file from disk — either way
// the result ends up as a plain URL string in form.logo.
const LogoField = ({ value, onChange, disabled }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const handlePickFile = () => fileInputRef.current?.click();

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;
    setUploadError('');
    setUploading(true);
    try {
      const res = await userAuthAPI.uploadLogo(file);
      onChange(res.url);
    } catch (err) {
      setUploadError(err.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="settings-field">
      <label className="settings-label"><i className="bi bi-image"></i> Company Logo</label>
      <div style={{ display: 'flex', alignItems: 'stretch', gap: 10 }}>
        <div className="settings-input-wrapper" style={{ flex: '1 1 0%', minWidth: 0 }}>
          <input
            type="text"
            className="settings-input"
            placeholder="https://example.com/logo.png"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled || uploading}
          />
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          onChange={handleFileSelected}
          style={{ display: 'none' }}
        />
        <button
          type="button"
          onClick={handlePickFile}
          disabled={disabled || uploading}
          style={{
            flex: '0 0 auto', whiteSpace: 'nowrap', minWidth: 120,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            padding: '0 18px', height: 48, borderRadius: 10,
            border: '1.5px solid #00A19D', background: uploading ? '#e6f7f6' : '#f0fdfa',
            color: '#00857E', fontSize: 14, fontWeight: 600,
            cursor: (disabled || uploading) ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1,
          }}
        >
          {uploading ? <><span className="settings-spinner"></span> Uploading...</> : <><i className="bi bi-upload"></i> Upload</>}
        </button>
      </div>
      {uploadError && <div className="settings-alert settings-alert-error" style={{ marginTop: 6 }}><i className="bi bi-exclamation-circle-fill"></i>{uploadError}</div>}
      {value && (
        <div style={{ marginTop: 10 }}>
          <img src={value} alt="Logo preview" style={{ maxHeight: 60, maxWidth: 180, borderRadius: 6, border: '1px solid #e2e8f0', objectFit: 'contain', background: '#fff' }}
            onError={(e) => { e.target.style.display = 'none'; }}
            onLoad={(e) => { e.target.style.display = 'block'; }}
          />
        </div>
      )}
    </div>
  );
};

const ProfileUpdate = ({ onClose, onProfileUpdated }) => {
  const [form, setForm]       = useState({ name:'', email:'', mobile:'', logo:'', website_url:'', company_name:'', address:'' });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const d = (() => { try { return JSON.parse(localStorage.getItem('user_data') || 'null'); } catch { return null; } })();
    if (d) setForm({ name: d.name||'', email: d.email||'', mobile: d.mobile||'', logo: d.logo||'', website_url: d.website_url||'', company_name: d.company_name||'', address: d.address||'' });
  }, []);

  const handle = (e) => { setForm(p => ({ ...p, [e.target.name]: e.target.value })); setError(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setLoading(true);
    try {
      const res = await userAuthAPI.updateProfile(form);
      const curr = JSON.parse(localStorage.getItem('user_data') || '{}');
      localStorage.setItem('user_data', JSON.stringify({ ...curr, ...res.user }));
      setSuccess('Profile updated successfully!');
      if (onProfileUpdated) onProfileUpdated(res.user);
    } catch (err) {
      setError(err.message || 'Failed to update profile.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings-page">
      <div className="settings-card">

        {/* Header */}
        <div className="settings-card-header">
          <div className="settings-title">
            <div className="settings-title-icon">
              <i className="bi bi-person-fill"></i>
            </div>
            <div>
              <h2>Update Profile</h2>
              <p>Manage your personal information</p>
            </div>
          </div>
          {onClose && (
            <button className="settings-close-btn" onClick={onClose}>
              <i className="bi bi-x-lg"></i>
            </button>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="settings-form">
          {error   && <div className="settings-alert settings-alert-error"><i className="bi bi-exclamation-circle-fill"></i>{error}</div>}
          {success && <div className="settings-alert settings-alert-success"><i className="bi bi-check-circle-fill"></i>{success}</div>}

          {/* Personal info */}
          <div className="settings-form-grid">
            <div className="settings-section-label">Personal Information</div>

            <Field name="name"   label="Full Name"    icon="bi-person"    placeholder="Your full name"    value={form.name}         onChange={handle} disabled={loading}/>
            <Field name="email"  label="Email"        icon="bi-envelope"  type="email" placeholder="Email address"  value={form.email}        onChange={handle} disabled={loading}/>
            <Field name="mobile" label="Mobile"       icon="bi-phone"     type="tel" placeholder="Mobile number"  value={form.mobile}       onChange={handle} disabled={loading}/>
          </div>

          {/* Company info */}
          <div className="settings-form-grid">
            <div className="settings-section-label">Company / Business</div>

            <Field name="company_name" label="Company Name"  icon="bi-building"    placeholder="Company or firm name"           value={form.company_name} onChange={handle} disabled={loading}/>
            <Field name="website_url"  label="Website URL"   icon="bi-globe"       placeholder="https://example.com"            value={form.website_url}  onChange={handle} disabled={loading}/>
            <LogoField value={form.logo} onChange={(url) => setForm(p => ({ ...p, logo: url }))} disabled={loading}/>
            <Field name="address"      label="Address"       icon="bi-geo-alt"     placeholder="Full address" full               value={form.address}      onChange={handle} disabled={loading}/>
          </div>

          <button type="submit" className="settings-submit-btn" disabled={loading}>
            {loading
              ? <><span className="settings-spinner"></span><span>Saving...</span></>
              : <><i className="bi bi-check-circle"></i><span>Save Profile</span></>
            }
          </button>
        </form>

      </div>
    </div>
  );
};

export default ProfileUpdate;
